import { useCallback, useEffect, useState } from "react";
import {
  COMBO_CAMPAIGN,
  DISCOUNT_TYPE,
  SELLER_USER_SEGMENT_TYPE,
  TYPE_LIMIT,
  isProductPromoProgramBudgetRequired,
} from "features/Campaign/constants";
import {
  applyUnlimitedPurchaseLimitToProductPromoTiers,
  getProductPromoTierBudgetValue,
} from "features/Campaign/utils/productPromoTargetCustomer";

function useOneOnProductForm({ form, dataTiersServer, isUpdate }) {
  const [dataSource, setDataSource] = useState([]);

  const syncProductPromoFormValues = (promotionCampaignTierGroupsProductPromo) => {
    const sellerSegmentType = form.getFieldValue("sellerSegmentType");
    const nextGroups = isProductPromoProgramBudgetRequired(sellerSegmentType)
      ? applyUnlimitedPurchaseLimitToProductPromoTiers(promotionCampaignTierGroupsProductPromo)
      : promotionCampaignTierGroupsProductPromo;

    form.setFieldsValue({
      promotionCampaignTierGroupsProductPromo: nextGroups,
    });

    return nextGroups;
  };

  const copyPromotionTierGroupsProductPromo = (allProducts) => {
    return [
      {
        type: COMBO_CAMPAIGN.DISCOUNT_PRODUCT,
        promotionCampaignTiers: allProducts
          ?.filter((itemData) => itemData?.id)
          ?.map((itemData) => {
            const newConditionProductGroups = {
              quantity: 1,
              conditionPromotionProducts: [
                {
                  id: itemData?.id,
                  productID: itemData?.id,
                  product: itemData,
                },
              ],
            };

            const sellerSegmentType = form.getFieldValue("sellerSegmentType");
            const isAllSellers = sellerSegmentType === SELLER_USER_SEGMENT_TYPE.ALL;
            const isLimitedSegment = sellerSegmentType
              ? [SELLER_USER_SEGMENT_TYPE.NO_ORDER, SELLER_USER_SEGMENT_TYPE.NOT_PURCHASED_PRODUCT].includes(
                  sellerSegmentType
                )
              : false;
            const limitType = isLimitedSegment ? TYPE_LIMIT.NO_LIMIT : TYPE_LIMIT.LIMIT;

            return {
              type: COMBO_CAMPAIGN.DISCOUNT_PRODUCT,
              discountType: DISCOUNT_TYPE.DISCOUNT_MONEY,
              limitType,
              discountAmount: undefined,
              maxBudgetPerSeller: undefined,
              maxQuantityPerSeller: isAllSellers ? undefined : undefined,
              conditionProductGroups: [newConditionProductGroups],
            };
          }),
      },
    ];
  };

  const convertPromotionTierGroupsProductPromo = useCallback(
    (allProducts) => {
      const sellerSegmentType = form.getFieldValue("sellerSegmentType");
      return {
        type: allProducts?.[0]?.type,
        promotionCampaignTiers: allProducts?.[0]?.promotionCampaignTiers
          ?.filter((tiers) => {
            const prod = tiers?.conditionProductGroups?.[0]?.conditionPromotionProducts?.[0];
            const productID = prod?.productID || prod?.product?.id || prod?.id;
            return !!productID;
          })
          ?.map((tiers) => {
            const prod = tiers?.conditionProductGroups?.[0]?.conditionPromotionProducts?.[0];
            const productID = prod?.productID || prod?.product?.id || prod?.id;
            const productInfo = prod?.product || {};

            const newConditionProductGroups = {
              quantity: 1,
              conditionPromotionProducts: [
                {
                  id: productID,
                  productID: productID,
                  product: productInfo,
                },
              ],
            };

            const maxQuantity = tiers?.maxQuantityPerSeller || 0;
            const limitType = maxQuantity > 0 ? TYPE_LIMIT.LIMIT : TYPE_LIMIT.NO_LIMIT;

            return {
              discountAmount: tiers?.discountAmount,
              maxBudgetPerSeller: getProductPromoTierBudgetValue(tiers, sellerSegmentType),
              discountType: tiers?.discountType,
              limitType: limitType,
              maxQuantityPerSeller: limitType === TYPE_LIMIT.NO_LIMIT ? undefined : maxQuantity,
              type: tiers?.type,
              conditionProductGroups: [newConditionProductGroups],
            };
          }),
      };
    },
    [form]
  );

  const convertGetProducts = useCallback((tiers) => {
    return tiers
      ?.flatMap((item) => item?.conditionProductGroups)
      ?.flatMap((item) => item?.conditionPromotionProducts)
      ?.flatMap((item) => item?.product);
  }, []);

  const handleGetDataSource = (value) => {
    if (value?.[0]?.promotionCampaignTiers) {
      const tierGroups = value;
      const newProducts =
        tierGroups?.[0]?.promotionCampaignTiers?.map((tier) => {
          const prod = tier?.conditionProductGroups?.[0]?.conditionPromotionProducts?.[0];
          const productID = prod?.productID || prod?.product?.id || prod?.id;
          const productInfo = prod?.product || {};
          const priceVat = productInfo?.productPrices?.priceVat || 0;
          return {
            id: productID,
            productID: productID,
            name: productInfo?.name || `Product ${productID}`,
            code: productInfo?.code || productID,
            productPrices: {
              price: priceVat,
              priceVat: priceVat,
            },
          };
        }) || [];

      const existingProductIds = new Set(dataSource.map((item) => item.id?.toString()));
      const filteredNewProducts = newProducts.filter((item) => !existingProductIds.has(item.id?.toString()));
      const mergedProducts = [...dataSource, ...filteredNewProducts];
      setDataSource(mergedProducts);

      const importedData = convertPromotionTierGroupsProductPromo(tierGroups);
      const { promotionCampaignTierGroupsProductPromo = [] } = form.getFieldsValue();
      const existingTiers = promotionCampaignTierGroupsProductPromo[0]?.promotionCampaignTiers || [];

      const importedTiersMap = new Map();
      importedData?.promotionCampaignTiers?.forEach((tier) => {
        const condPromo = tier?.conditionProductGroups?.[0]?.conditionPromotionProducts;
        const prod = Array.isArray(condPromo) ? condPromo[0] : condPromo;
        const pId = (prod?.productID || prod?.id)?.toString();
        if (pId) {
          importedTiersMap.set(pId, tier);
        }
      });

      const existingTiersMap = new Map();
      existingTiers.forEach((tier) => {
        const condPromo = tier?.conditionProductGroups?.[0]?.conditionPromotionProducts;
        const prod = Array.isArray(condPromo) ? condPromo[0] : condPromo;
        const pId = (prod?.productID || prod?.id)?.toString();
        if (pId) {
          existingTiersMap.set(pId, tier);
        }
      });

      const finalTiers = mergedProducts.map((product) => {
        const pId = product.id?.toString();
        if (pId && importedTiersMap.has(pId)) {
          return importedTiersMap.get(pId);
        }
        if (pId && existingTiersMap.has(pId)) {
          return existingTiersMap.get(pId);
        }
        return copyPromotionTierGroupsProductPromo([product])[0].promotionCampaignTiers[0];
      });

      syncProductPromoFormValues([
        {
          type: COMBO_CAMPAIGN.DISCOUNT_PRODUCT,
          promotionCampaignTiers: finalTiers,
        },
      ]);
    } else if (Array.isArray(value)) {
      const existingProductIds = new Set(dataSource.map((item) => item.id?.toString()));
      const filteredNewProducts = value.filter((item) => !existingProductIds.has(item.id?.toString()));
      const mergedProducts = [...dataSource, ...filteredNewProducts];
      setDataSource(mergedProducts);

      const { promotionCampaignTierGroupsProductPromo = [] } = form.getFieldsValue();
      const existingTiers = promotionCampaignTierGroupsProductPromo[0]?.promotionCampaignTiers || [];

      const existingTiersMap = new Map();
      existingTiers.forEach((tier) => {
        const condPromo = tier?.conditionProductGroups?.[0]?.conditionPromotionProducts;
        const prod = Array.isArray(condPromo) ? condPromo[0] : condPromo;
        const pId = (prod?.productID || prod?.id)?.toString();
        if (pId) {
          existingTiersMap.set(pId, tier);
        }
      });

      const finalTiers = mergedProducts.map((product) => {
        const pId = product.id?.toString();
        if (pId && existingTiersMap.has(pId)) {
          return existingTiersMap.get(pId);
        }
        return copyPromotionTierGroupsProductPromo([product])[0].promotionCampaignTiers[0];
      });

      syncProductPromoFormValues([
        {
          type: COMBO_CAMPAIGN.DISCOUNT_PRODUCT,
          promotionCampaignTiers: finalTiers,
        },
      ]);
    } else {
      setDataSource(value);
    }
  };

  const [idDeleted, setIdDeleted] = useState();

  const confirmDelete = (id) => {
    const { promotionCampaignTierGroupsProductPromo = [] } = form.getFieldsValue();
    const promotionCampaignTiers = promotionCampaignTierGroupsProductPromo[0]?.promotionCampaignTiers || [];
    const newDataSource = dataSource.filter((item) => item.id !== id);

    const updatedPromotionCampaignTiers = promotionCampaignTiers.filter((tier) =>
      tier.conditionProductGroups?.some((group) => {
        const condPromo = group.conditionPromotionProducts;
        const prod = Array.isArray(condPromo) ? condPromo[0] : condPromo;
        const idProduct = prod?.productID || prod?.id || prod?.product?.id;
        return idProduct?.toString() !== id?.toString();
      })
    );

    form.setFieldsValue({
      promotionCampaignTierGroupsProductPromo: [
        { type: COMBO_CAMPAIGN.DISCOUNT_PRODUCT, promotionCampaignTiers: updatedPromotionCampaignTiers },
      ],
    });

    setIdDeleted(id);
    setDataSource(newDataSource);
  };

  useEffect(() => {
    if (isUpdate) {
      const newData = convertPromotionTierGroupsProductPromo(dataTiersServer);
      form.setFieldsValue({
        promotionCampaignTierGroupsProductPromo: [newData],
      });

      setDataSource(convertGetProducts(newData?.promotionCampaignTiers));
    }
  }, [dataTiersServer, isUpdate, form, convertPromotionTierGroupsProductPromo, convertGetProducts]);

  return {
    dataSource,
    handleGetDataSource,
    confirmDelete,
    convertGetProducts,
    idDeleted,
  };
}

export default useOneOnProductForm;
